import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNavGuard } from '../context/NavGuardContext';
import { billingService } from '../services/api';
import logo from '../assets/logo.svg';
import SubscriptionBadge from './SubscriptionBadge';
import { Menu, X } from 'lucide-react';

const Layout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { guard } = useNavGuard();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);

  useEffect(() => {
    billingService.isPaymentsEnabled()
      .then((res) => setPaymentsEnabled(res.data.enabled))
      .catch(() => {});
  }, [location.pathname]);

  const handleDashboardNav = (e: React.MouseEvent) => {
    if (guard) {
      e.preventDefault();
      guard();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <nav className="sticky top-0 z-100 bg-white/80 backdrop-blur-md border-b border-[#E4E4E7] px-4 sm:px-8 h-14 flex items-center justify-between">
        <div className="flex items-center">
          <NavLink to="/dashboard" className="flex items-center no-underline" onClick={(e) => { closeMenu(); handleDashboardNav(e); }}>
            <img src={logo} alt="3atech resume" className="h-7 w-auto block" />
          </NavLink>
        </div>

        <div className="hidden md:flex items-center gap-0.5">
          {isAuthenticated ? (
            <>
              <NavLink
                to="/dashboard"
                onClick={handleDashboardNav}
                className={({ isActive }) =>
                  `nav-link-app ${isActive ? 'active' : ''}`
                }
              >
                Dashboard
              </NavLink>
              <NavLink
                to="/applications"
                className={({ isActive }) =>
                  `nav-link-app ${isActive ? 'active' : ''}`
                }
              >
                Applications
              </NavLink>
              {paymentsEnabled && (
                <NavLink
                  to="/pricing"
                  className={({ isActive }) =>
                    `nav-link-app ${isActive ? 'active' : ''}`
                  }
                >
                  Pricing
                </NavLink>
              )}
              <NavLink
                to="/profile"
                className={({ isActive }) =>
                  `nav-link-app ${isActive ? 'active' : ''}`
                }
              >
                Profile
              </NavLink>
              {user?.roles?.includes('ADMIN') && (
                <NavLink
                  to="/admin-dashboard"
                  className={({ isActive }) =>
                    `nav-link-app ${isActive ? 'active' : ''}`
                  }
                >
                  Admin
                </NavLink>
              )}
              <SubscriptionBadge />
              <button onClick={handleLogout} className="nav-link-app btn-logout-app">
                Logout
              </button>
            </>
          ) : (
            <>
              <NavLink to="/" className="nav-link-app">
                Home
              </NavLink>
            </>
          )}
        </div>

        <button
          className="md:hidden w-9 h-9 flex items-center justify-center text-zinc-600 hover:text-foreground transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-14 z-50 bg-white border-b border-zinc-200 shadow-elevated">
          <div className="flex flex-col p-4 gap-1">
            {isAuthenticated ? (
              <>
                <NavLink
                  to="/dashboard"
                  onClick={(e) => { closeMenu(); handleDashboardNav(e); }}
                  className={({ isActive }) =>
                    `w-full px-4 py-3 text-sm font-medium rounded-btn transition-colors ${isActive ? 'bg-brand-50 text-brand-600' : 'text-zinc-600 hover:bg-zinc-50'}`
                  }
                >
                  Dashboard
                </NavLink>
                <NavLink
                  to="/applications"
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    `w-full px-4 py-3 text-sm font-medium rounded-btn transition-colors ${isActive ? 'bg-brand-50 text-brand-600' : 'text-zinc-600 hover:bg-zinc-50'}`
                  }
                >
                  Applications
                </NavLink>
                {paymentsEnabled && (
                  <NavLink
                    to="/pricing"
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `w-full px-4 py-3 text-sm font-medium rounded-btn transition-colors ${isActive ? 'bg-brand-50 text-brand-600' : 'text-zinc-600 hover:bg-zinc-50'}`
                    }
                  >
                    Pricing
                  </NavLink>
                )}
                <NavLink
                  to="/profile"
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    `w-full px-4 py-3 text-sm font-medium rounded-btn transition-colors ${isActive ? 'bg-brand-50 text-brand-600' : 'text-zinc-600 hover:bg-zinc-50'}`
                  }
                >
                  Profile
                </NavLink>
                {user?.roles?.includes('ADMIN') && (
                  <NavLink
                    to="/admin-dashboard"
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `w-full px-4 py-3 text-sm font-medium rounded-btn transition-colors ${isActive ? 'bg-brand-50 text-brand-600' : 'text-zinc-600 hover:bg-zinc-50'}`
                    }
                  >
                    Admin
                  </NavLink>
                )}
                <div className="border-t border-zinc-100 my-2" />
                <button
                  onClick={() => { handleLogout(); closeMenu(); }}
                  className="w-full px-4 py-3 text-sm font-medium text-left text-red-500 hover:bg-red-50 rounded-btn transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <NavLink
                to="/"
                onClick={closeMenu}
                className="w-full px-4 py-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 rounded-btn transition-colors"
              >
                Home
              </NavLink>
            )}
          </div>
        </div>
      )}

      <main className="flex-1 p-4 sm:p-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
